import { useAuth } from '@/hooks/useAuth';
import { BarChart3, Sparkles, Shield, Key, ArrowRight, ShieldCheck, AppWindow, Lightbulb, UserSquare2, BookOpen } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, studioRecommendations, studioProjects, studioPsa } from '@/lib/api';
import { useEffect, useState } from 'react';

interface Recommendation {
  starter_id: string;
  starter_name: string;
  confidence: number;
  reasoning: string;
  estimated_revenue_monthly?: number;
}

interface Project {
  id: string;
  name: string;
  status: string;
  deployment_id?: string | null;
}

interface PsaStatus {
  connected: boolean;
  cached_data?: { company_count?: number; sample_companies?: string[] };
}

const AI_QUICK_ACTIONS = [
  { to: '/playground', icon: Sparkles, title: 'AI Playground', description: 'Test models and build prompts' },
  { to: '/governance', icon: Shield, title: 'Governance', description: 'Scan AI systems for compliance' },
  { to: '/usage', icon: BarChart3, title: 'Usage & Billing', description: 'Monitor usage and costs' },
  { to: '/api-keys', icon: Key, title: 'API Keys', description: 'Manage SDK access keys' },
];

const BUILD_QUICK_ACTIONS = [
  { to: '/ideas', icon: Lightbulb, title: 'Idea Library', description: 'AI-curated app ideas for your MSP' },
  { to: '/starters', icon: BookOpen, title: 'Starters', description: 'Pre-built app templates' },
  { to: '/apps', icon: AppWindow, title: 'My Apps', description: 'Manage your deployed apps' },
  { to: '/clients', icon: UserSquare2, title: 'Client Tenants', description: 'Manage client workspaces' },
];

export function Dashboard() {
  const { user, studio } = useAuth();
  const navigate = useNavigate();
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [psaStatus, setPsaStatus] = useState<PsaStatus | null>(null);

  useEffect(() => {
    async function load() {
      const [recsData, projectsData, psaData] = await Promise.allSettled([
        studioRecommendations.get(),
        studioProjects.list(),
        studioPsa.status(),
      ]);
      if (recsData.status === 'fulfilled') {
        setRecommendations(((recsData.value.recommendations ?? []) as Recommendation[]).slice(0, 3));
      }
      if (projectsData.status === 'fulfilled') {
        setProjects(((projectsData.value.projects ?? []) as Project[]).slice(0, 4));
      }
      if (psaData.status === 'fulfilled') {
        setPsaStatus(psaData.value as PsaStatus);
      }
    }
    void load();
  }, []);

  const deployedCount = projects.filter((p) => p.status === 'deployed').length;
  const psaConnected = psaStatus?.connected;
  const sampleCompany = psaStatus?.cached_data?.sample_companies?.[0];

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-foreground">
          Welcome back, {user?.user.name?.split(' ')[0]}
        </h1>
        <p className="text-muted-foreground mt-1">
          {studio
            ? `${deployedCount} app${deployedCount !== 1 ? 's' : ''} deployed · ${studio.client_count} clients`
            : 'Your AI Studio workspace'}
        </p>
      </div>

      {/* PSA wow moment */}
      {psaConnected && sampleCompany && recommendations[0] && (
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-5 mb-6">
          <p className="text-sm text-blue-700 font-medium mb-1">
            Here's what your {recommendations[0].starter_name} could look like for{' '}
            <strong>{sampleCompany}</strong> right now:
          </p>
          <p className="text-xs text-blue-500">
            Your PSA has {psaStatus?.cached_data?.company_count ?? 0} companies ready to go.
          </p>
          <button
            onClick={() => navigate('/starters')}
            className="mt-3 text-xs bg-blue-600 text-white px-4 py-1.5 rounded-lg hover:bg-blue-700"
          >
            Build {recommendations[0].starter_name} →
          </button>
        </div>
      )}

      {/* PSA nudge */}
      {!psaConnected && (
        <div className="bg-amber-50 border border-amber-100 rounded-xl p-5 mb-6">
          <p className="text-sm font-medium text-amber-800">Connect your PSA to unlock personalized recommendations</p>
          <p className="text-xs text-amber-600 mt-1">MSPs with PSA connected see 40% faster first deploy.</p>
          <button
            onClick={() => navigate('/psa')}
            className="mt-3 text-xs bg-amber-600 text-white px-4 py-1.5 rounded-lg hover:bg-amber-700"
          >
            Connect PSA →
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Build section */}
        <div>
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Build</h2>
          <div className="grid grid-cols-2 gap-3">
            {BUILD_QUICK_ACTIONS.map((action) => (
              <Link
                key={action.to}
                to={action.to}
                className="flex flex-col gap-2 p-4 rounded-xl border border-border hover:border-primary/50 hover:bg-accent/30 transition-colors group bg-card"
              >
                <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <action.icon className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">{action.title}</p>
                  <p className="text-xs text-muted-foreground">{action.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* AI Tools section */}
        <div>
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">AI Tools</h2>
          <div className="grid grid-cols-2 gap-3">
            {AI_QUICK_ACTIONS.map((action) => (
              <Link
                key={action.to}
                to={action.to}
                className="flex flex-col gap-2 p-4 rounded-xl border border-border hover:border-primary/50 hover:bg-accent/30 transition-colors group bg-card"
              >
                <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <action.icon className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">{action.title}</p>
                  <p className="text-xs text-muted-foreground">{action.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Studio stats (if subscribed) */}
      {studio && (
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="border border-border rounded-xl p-4 bg-card">
            <p className="text-xs text-muted-foreground mb-1">Deployed Apps</p>
            <p className="text-2xl font-bold text-foreground">{studio.app_count}</p>
          </div>
          <div className="border border-border rounded-xl p-4 bg-card">
            <p className="text-xs text-muted-foreground mb-1">Clients</p>
            <p className="text-2xl font-bold text-foreground">{studio.client_count}</p>
          </div>
          <div className="border border-border rounded-xl p-4 bg-card">
            <p className="text-xs text-muted-foreground mb-1">AI Credits Used</p>
            <p className="text-2xl font-bold text-foreground">
              ${studio.credits_used_usd.toFixed(0)}
              <span className="text-sm text-muted-foreground font-normal"> / ${studio.included_credits_usd}</span>
            </p>
          </div>
        </div>
      )}

      {/* Recent projects */}
      {projects.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Recent Projects</h2>
            <Link to="/apps" className="text-xs text-primary hover:underline">View all</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {projects.map((project) => (
              <div
                key={project.id}
                className="border border-border rounded-xl p-4 bg-card hover:border-primary/50 cursor-pointer transition-colors"
                onClick={() => navigate(`/builder/${project.id}`)}
              >
                <h3 className="text-sm font-medium text-foreground truncate">{project.name}</h3>
                <span className={`text-xs mt-1 inline-block px-2 py-0.5 rounded-full ${
                  project.status === 'deployed' ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground'
                }`}>
                  {project.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Security tile */}
      <SecurityTile />

      {/* SDK quickstart */}
      <div className="mt-8 p-6 rounded-xl border border-border bg-card">
        <h2 className="text-sm font-semibold text-foreground mb-2">Studio SDK</h2>
        <p className="text-sm text-muted-foreground mb-3">
          Embed Studio-built apps into any website or framework.
        </p>
        <pre className="p-4 rounded-lg bg-muted text-sm font-mono overflow-x-auto">
          <code>npm install @theoneaiplatform/studio-sdk</code>
        </pre>
      </div>
    </div>
  );
}

function SecurityTile() {
  const { data } = useQuery({
    queryKey: ['security-status'],
    queryFn: () => api<{ connected: boolean; summary: { posture_score: number | null; open_findings: number; critical_vulns: number; last_scan_at: string | null } | null }>('security/integration/status'),
    staleTime: 60_000,
  });

  const connected = data?.connected ?? false;
  const summary = data?.summary;
  const score = summary?.posture_score;
  const scoreColor = score == null ? '#6b7280' : score >= 80 ? '#10b981' : score >= 60 ? '#f59e0b' : '#ef4444';

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-indigo-500" />
          <span className="font-semibold text-sm text-foreground">The One Security</span>
          {connected && (
            <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-600">Connected</span>
          )}
        </div>
        <Link to="/security" className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors">
          {connected ? 'View details' : 'Connect'} <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
      {connected && summary ? (
        <div className="grid grid-cols-3 divide-x divide-border">
          <div className="px-6 py-4 text-center">
            <p className="text-xs text-muted-foreground mb-1">Posture</p>
            <p className="text-2xl font-bold" style={{ color: scoreColor }}>{score ?? '—'}</p>
          </div>
          <div className="px-6 py-4 text-center">
            <p className="text-xs text-muted-foreground mb-1">Open Findings</p>
            <p className="text-2xl font-bold text-foreground">{summary.open_findings}</p>
          </div>
          <div className="px-6 py-4 text-center">
            <p className="text-xs text-muted-foreground mb-1">Critical Vulns</p>
            <p className="text-2xl font-bold text-destructive">{summary.critical_vulns}</p>
          </div>
        </div>
      ) : (
        <div className="px-6 py-5 flex items-center gap-3 text-sm text-muted-foreground">
          <Shield className="h-4 w-4 flex-shrink-0" />
          Connect The One Security to see live vulnerability and posture data here.
        </div>
      )}
    </div>
  );
}
